import { useState, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  Edit,
  ListChecks,
  FileCode,
  Database,
} from "lucide-react";
import { MarkupPanel, MarkupPanelHandle } from "./MarkupPanel";

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: any[];
  isGeneral: boolean;
}

interface AnalysisEditorTabsProps {
  // Current tab
  currentContent: string;
  onCurrentChange: (content: string) => void;

  // Original tab (read-only)
  originalContent: string;

  // Synthesis tab
  synthesisContent: string;
  onSynthesisChange?: (content: string) => void;
  onSetSynthesisContent?: (content: string) => void; // For AI-generated content

  // Key Points tab
  keyPoints: string[];

  // Context data
  contextStatements: string[];

  // Raw/Debug data
  comments: Comment[];
  changes: unknown[];
  attachments: unknown[];

  // Comment tracking for aggregation
  onCurrentCommentsChange?: (comments: Comment[]) => void;
  onSynthesisCommentsChange?: (comments: Comment[]) => void;
  onOriginalCommentsChange?: (comments: Comment[]) => void;

  // Active tab tracking
  activeTab?: string; // Controlled tab state from parent
  onActiveTabChange?: (tab: string) => void;

  // Refs to MarkupPanel instances
  currentPanelRef?: React.RefObject<MarkupPanelHandle>;
  synthesisPanelRef?: React.RefObject<MarkupPanelHandle>;
  originalPanelRef?: React.RefObject<MarkupPanelHandle>;
}

export const AnalysisEditorTabs = ({
  currentContent,
  onCurrentChange,
  comments,
  originalContent,
  synthesisContent,
  onSynthesisChange,
  onSetSynthesisContent,
  keyPoints,
  contextStatements,
  changes,
  attachments,
  onCurrentCommentsChange,
  onSynthesisCommentsChange,
  onOriginalCommentsChange,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  currentPanelRef,
  synthesisPanelRef,
  originalPanelRef,
}: AnalysisEditorTabsProps) => {
  const [internalActiveTab, setInternalActiveTab] = useState("current");
  
  // Use controlled tab if provided, otherwise use internal state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabChange = (tab: string) => {
    setInternalActiveTab(tab);
    onActiveTabChange?.(tab);
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className="h-full flex flex-col"
    >
      <TabsList className="w-full rounded-none border-b shrink-0">
        <TabsTrigger value="current" className="flex items-center gap-1.5">
          <Edit className="w-3.5 h-3.5" />
          Current
        </TabsTrigger>
        <TabsTrigger value="synthesis" className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Synthesis
        </TabsTrigger>
        <TabsTrigger value="keypoints" className="flex items-center gap-1.5">
          <ListChecks className="w-3.5 h-3.5" />
          Key Points
        </TabsTrigger>
        <TabsTrigger value="original" className="flex items-center gap-1.5">
          <FileCode className="w-3.5 h-3.5" />
          Original
        </TabsTrigger>
        <TabsTrigger value="raw" className="flex items-center gap-1.5">
          <Database className="w-3.5 h-3.5" />
          Raw
        </TabsTrigger>
      </TabsList>

      {/* Current Tab - Independent MarkupPanel */}
      <TabsContent value="current" className="flex-1 min-h-0 m-0 p-4" forceMount={activeTab !== "current" ? true : undefined} hidden={activeTab !== "current"}>
        <div className="h-full">
          <MarkupPanel
            ref={currentPanelRef}
            content={currentContent}
            onChange={onCurrentChange}
            onSetSynthesisContent={onSynthesisChange}
            onCommentsChange={onCurrentCommentsChange}
            editorTestId="current-tab-editor"
          />
        </div>
      </TabsContent>

      {/* Synthesis Tab - Independent MarkupPanel */}
      <TabsContent value="synthesis" className="flex-1 min-h-0 m-0 p-4" forceMount={activeTab !== "synthesis" ? true : undefined} hidden={activeTab !== "synthesis"}>
        <div className="h-full">
          <MarkupPanel
            ref={synthesisPanelRef}
            content={synthesisContent}
            onChange={onSynthesisChange || (() => {})}
            onSetSynthesisContent={onSynthesisChange}
            onCommentsChange={onSynthesisCommentsChange}
          />
        </div>
      </TabsContent>

      {/* Key Points Tab - List view */}
      <TabsContent value="keypoints" className="flex-1 min-h-0 m-0" forceMount hidden={activeTab !== "keypoints"}>
        <ScrollArea className="h-full">
          <div className="p-4 space-y-2">
            {keyPoints.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No key points extracted yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {keyPoints.map((point, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm p-2 rounded-md border bg-card"
                  >
                    <span className="text-primary font-medium mt-0.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      {/* Original Tab - Read-only MarkupPanel */}
      <TabsContent value="original" className="flex-1 min-h-0 m-0 p-4" forceMount={activeTab !== "original" ? true : undefined} hidden={activeTab !== "original"}>
        <div className="h-full">
          <MarkupPanel
            ref={originalPanelRef}
            content={originalContent}
            onChange={() => {}} // Read-only
            onSetSynthesisContent={onSynthesisChange}
            onCommentsChange={onOriginalCommentsChange}
          />
        </div>
      </TabsContent>

      {/* Raw Tab - Shows raw JSON state */}
      <TabsContent value="raw" className="flex-1 min-h-0 m-0" forceMount hidden={activeTab !== "raw"}>
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {/* Synthesis */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Synthesis
              </h3>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono whitespace-pre-wrap border">
                {synthesisContent || "No synthesis"}
              </div>
            </div>

            {/* Context Statements */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Context
              </h3>
              <div className="bg-muted/30 rounded-md p-3 text-xs border">
                {contextStatements.length === 0 ? (
                  <span className="text-muted-foreground">
                    No context statements
                  </span>
                ) : (
                  <ul className="space-y-1">
                    {contextStatements.map((statement, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="text-primary">•</span>
                        <span>{statement}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Changes */}
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Changes ({changes.length})
              </h3>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono border">
                <pre>{JSON.stringify(changes, null, 2)}</pre>
              </div>
            </div>

            {/* Annotations */}
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Annotations ({comments.length})
              </h3>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono border">
                <pre>{JSON.stringify(comments, null, 2)}</pre>
              </div>
            </div>

            {/* Attachments */}
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Attachments ({attachments.length})
              </h3>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono border">
                <pre>{JSON.stringify(attachments, null, 2)}</pre>
              </div>
            </div>
          </div>
        </ScrollArea>
      </TabsContent>
    </Tabs>
  );
};
